/**
 * Пояснення помилки від сервера — те, що можна показати користувачу.
 *
 * ⚠️ Не читайте `error.message`: ядровий `RestClientService` лишає там текст
 * ky — «Request failed with status code 422: POST https://…/ship». Це адреса
 * запиту, а не причина відмови; користувачу вона нічого не каже, а справжнє
 * пояснення (яка позиція, на якому складі й скільки бракує) при цьому
 * губиться.
 *
 * Розібране тіло відповіді клієнт кладе в `error.response`, а HTTP-код —
 * у `error.statusCode`.
 *
 * Якщо сервер пояснення не дав (500, обрив зв'язку), повертаємо `fallback`
 * виклику: загальна фраза доречніша за технічний слід.
 */

interface ErrorLike {
    response?: unknown
    statusCode?: number
}

/** Перше непорожнє поле відповіді, яке несе текст для людини. */
const textOf = (body: unknown): string | null => {
    if (typeof body === 'string' && body.trim() !== '') return body.trim()

    if (body === null || typeof body !== 'object') return null

    const record = body as Record<string, unknown>

    for (const key of ['message', 'detail', 'title', 'error']) {
        const value = record[key]

        if (typeof value === 'string' && value.trim() !== '') return value.trim()
    }

    // Помилки валідації ядра: список порушень по полях
    const violations = record['violations']

    if (Array.isArray(violations) && violations.length > 0) {
        const lines = violations
            .map((item) => {
                const violation = (item ?? {}) as Record<string, unknown>
                const text = violation['message']

                return typeof text === 'string' ? text : null
            })
            .filter((line): line is string => line !== null)

        if (lines.length > 0) return lines.join(' ')
    }

    return null
}

export const serverMessage = (error: unknown, fallback: string): string => {
    if (error === null || typeof error !== 'object') return fallback

    const source = error as ErrorLike & { message?: unknown }
    const explained = textOf(source.response)

    if (explained !== null) return explained

    /**
     * Помилка від HTTP-клієнта, яку сервер не пояснив (500, обрив зв'язку):
     * її `message` — технічний слід ky, показувати його не можна.
     *
     * А от звичайний `throw new Error('Додайте хоча б один файл')` із нашого ж
     * коду несе готовий текст для людини — його показуємо. Розрізняє їх
     * наявність `response`/`statusCode`: у локально кинутої помилки їх немає.
     */
    const isHttpError = 'response' in source || 'statusCode' in source

    if (!isHttpError && typeof source.message === 'string' && source.message.trim() !== '') {
        return source.message.trim()
    }

    return fallback
}

/** HTTP-код відповіді, якщо клієнт його зберіг. */
export const serverStatus = (error: unknown): number | null => {
    if (error === null || typeof error !== 'object') return null

    const status = (error as ErrorLike).statusCode

    return typeof status === 'number' ? status : null
}
